import { gql } from '@apollo/client';
import { put, retry, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import SagaStatus from '../../../../Data/SagaStatus';
import ValidationStatus from '../../../../Data/ValidationStatus';
import PHONE_CONSTRAINT_NAMES from '../../../../Data/phoneConstraintNames';
import { query } from '../../../../Services/Gateway/Apollo';
import {
  setComponentValidationStatus,
  setPhoneValidationConstraint,
  setValidationConstraintStatus,
} from '../../slices/components';
import { setComponentIdToSagaStatus } from '../../slices/dataMappings';
import { handleAnswerQuestionSideEffectsSaga } from '../../../../Hooks/actionCreators';

export const VALIDATE_PHONE_NUMBER = gql`
  query ValidatePhone($phone: PhoneInput!) {
    validatePhone(phone: $phone) {
      isValidNumber
    }
  }
`;

const validatePhoneQuery = async ({ variables }) => {
  try {
    const response = await query({
      query: VALIDATE_PHONE_NUMBER,
      variables,
    });
    return response.data.validatePhone.isValidNumber;
  } catch (error) {
    return false;
  }
};

/**
 * Worker - requested by below function and carries out any async work
 */
export function* worker(action) {
  const { componentId, number, country, iFrameId, constraints, formId } =
    action.payload;

  yield put(
    setComponentIdToSagaStatus({
      componentId,
      status: SagaStatus.PENDING,
    })
  );

  const requestArguments = {
    phone: {
      number,
      country,
      validationMethod: 'gbpingphone',
    },
  };

  const SECOND = 1000;
  const args = {
    variables: requestArguments,
  };

  yield put(
    setPhoneValidationConstraint({
      componentId,
      status: ValidationStatus.PENDING,
    })
  );

  const isValid = yield retry(3, 10 * SECOND, validatePhoneQuery, args);

  if (constraints) {
    const constraintsArray = Object.keys(constraints);
    // eslint-disable-next-line no-restricted-syntax
    for (const constraintName of constraintsArray) {
      if (PHONE_CONSTRAINT_NAMES.includes(constraintName)) {
        if (isValid) {
          yield put(
            setValidationConstraintStatus({
              componentId,
              constraintName,
              status: ValidationStatus.VALID,
            })
          );
          /**
           * validate the rest of the components before the handle expose event
           */
          yield put(
            setComponentValidationStatus({
              componentId,
              countryCode: country,
              iFrameId,
              formId,
            })
          );
          yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
        } else {
          yield put(
            setValidationConstraintStatus({
              componentId,
              constraintName,
              status: ValidationStatus.INVALID,
            })
          );
          yield put(
            setComponentValidationStatus({
              componentId,
              countryCode: country,
              iFrameId,
              formId,
            })
          );
        }
      }
    }
  }

  yield put(
    setComponentIdToSagaStatus({
      componentId,
      status: SagaStatus.COMPLETE,
    })
  );
}

export const validatePhoneSaga = ({
  country,
  number,
  componentId,
  iFrameId,
  constraints,
  formId,
}) => ({
  type: ActionTypes.VALIDATE_PHONE_NUMBER,
  payload: {
    country,
    number,
    componentId,
    iFrameId,
    constraints,
    formId,
  },
});

/**
 * Listens for every VALIDATE_PHONE action that has been dispatched and will handle the saga of validating a phone number
 */
export default function* validatePhone() {
  yield takeEvery(ActionTypes.VALIDATE_PHONE_NUMBER, worker);
}
